import { useEffect, useMemo, useState } from 'react';
import { Account, InventoryAsset } from '../../../types';
import { getInventoryLoadingSubject } from '../../../store/Inventory';
import { getNestedValue } from '../../../utils/helpers';
import { getUserAccountById, GridPreferences } from '../../../store/User';
import { getAccountByIdOrSfId } from '../../../store/Accounts';
import {
  configAccountIdFor,
  isCardFieldVisible,
  useAccountGridConfig,
  useColumnsTurnedOn
} from '../../../store/AccountCustomization';
import { projectsRelatedInventoryColumns } from './ProjectsRelatedInventoryGridColumnDefs';
import { TAGS_FIELD } from '../../Compounds/UpstackDataGrid/tagColumns';

export interface AssetDetailField {
  colId: string;
  title: string;
  value: string;
}

// Tags are excluded with the CTA columns: a detail row renders its value as text, and tags are objects.
const EXCLUDED_FIELDS = ['details', 'id', 'detailCTA', 'sfId', TAGS_FIELD];

const getValue = (asset: Partial<InventoryAsset>, key: string) => {
  if (key === 'productDisplay') return asset.product?.nameAbbreviation || asset.product?.name || '';
  if (key.includes('.')) return getNestedValue(asset, key);
  if (key === 'account_id' && asset?.account_id)
    return asset?.accountName || getUserAccountById(asset?.account_id)?.name;

  const val = asset[key as keyof InventoryAsset] as string;
  return val || '';
};

// The columns the Related Inventory table can show this user, minus Risk (grid-only) and CTA/id; listed even when empty.
const buildFieldList = (
  asset: Partial<InventoryAsset>,
  config: GridPreferences | undefined,
  turnedOn: Record<string, boolean>
): AssetDetailField[] =>
  projectsRelatedInventoryColumns(false)
    .filter((col) => isCardFieldVisible(col.field, col.defaultHidden, config, turnedOn))
    .map((col) => ({
      colId: col.field,
      title: col.headerName || '',
      value: getValue(asset, col.field) || ''
    }))
    .filter((field) => !EXCLUDED_FIELDS.includes(field.colId));

// Field list and account lookup for an asset's Details section — shared between the
// project-asset drawer and the 1 Project : 1 Asset full-screen page. Accepts a partial
// asset because the risk endpoint's related-inventory items are typed Partial<InventoryAsset>.
// `projectAccountId` is the owning account of the project the asset is being viewed under; it
// resolves the column config the same way the Related Inventory table does.
export const useProjectAssetDetails = (asset: Partial<InventoryAsset>, projectAccountId?: string) => {
  const [assetLoading, setAssetLoading] = useState<boolean>(false);
  const [account, setAccount] = useState<Account | null>(null);
  const accountGridConfig = useAccountGridConfig(
    configAccountIdFor(projectAccountId, asset),
    'related_project_assets_grid'
  );
  const turnedOn = useColumnsTurnedOn('related_project_assets_grid', accountGridConfig);

  const fieldList = useMemo(
    () => (asset?.id ? buildFieldList(asset, accountGridConfig, turnedOn) : []),
    [asset, accountGridConfig, turnedOn]
  );

  useEffect(() => {
    const inventoryLoadingSub = getInventoryLoadingSubject().subscribe((loading: boolean) => {
      setAssetLoading(loading);
    });

    return () => {
      if (inventoryLoadingSub) inventoryLoadingSub.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!asset?.id) return;

    if (asset.account_id && asset.account_id !== account?.sfId) {
      // Guard against a slow fetch resolving after unmount or after the asset changed.
      let cancelled = false;
      getAccountByIdOrSfId(asset.account_id).then((fetched) => {
        if (fetched && !cancelled) setAccount(fetched);
      });

      return () => {
        cancelled = true;
      };
    }
  }, [asset]);

  return { fieldList, assetLoading, account };
};
